import { DestroyRef, Injectable, signal, WritableSignal } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, Observable, of, tap } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from '@app/environments/environment';
import { LoginResponse } from './login/types/login-response.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IUser } from './user.interface';
import { ILogin, ILoginSuccess } from './login/interfaces';
import { ResultBody } from '@app/public/models/ResultBody';
import { IS_WITHOUT } from '@app/core/interceptors/loader.interceptor';

const routes = {
  login: '/api/auth/seller/login',
  activateAccount: '/api/auth/confirm-account',
};

// Token refreshed 5 minutes before expiration time
const TOKEN_EXPIRY_THRESHOLD_MINUTES = 5;

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private destroyRef: DestroyRef
  ) {}

  get user(): WritableSignal<IUser | null> {
    const token = localStorage.getItem('token');
    return signal(token ? this.jwtHelper.decodeToken(token) : null);
  }

  isAuthenticated(): boolean {
    return !this.jwtHelper.isTokenExpired();
  }

  login(body: ILogin): Observable<ResultBody<LoginResponse>> {
    return this.http.post<ResultBody<LoginResponse>>(`${routes.login}`, body);
  }

  handelLoginSuccess(data: ResultBody<LoginResponse>): void {
    const loginSuccessData = data as ResultBody<ILoginSuccess>;
    this.storeTokens(loginSuccessData.data);
    this.scheduleTokenRefresh(loginSuccessData.data.token);
  }

  logout(): void {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    this.router.navigate(['']).then();
  }

  refreshToken(): Observable<LoginResponse | null> {
    const refresh_token = localStorage.getItem('refresh_token');
    if (!refresh_token) {
      return of();
    }
    const context = new HttpContext().set(IS_WITHOUT, true);

    return this.http
      .post<LoginResponse>(
        `${environment.serverUrl}/token/refresh`,
        {
          refresh_token,
        },
        { context }
      )
      .pipe(
        catchError(() => of()),
        tap((data) => {
          const loginSuccessData = data as ILoginSuccess;
          this.storeTokens(loginSuccessData);
          this.scheduleTokenRefresh(loginSuccessData.token);
        })
      );
  }

  activateAccount(token: string): Observable<any> {
    const params = new HttpParams().set('token', token);
    return this.http.get<any>('/api/auth/confirm-account', { params });
  }

  private scheduleTokenRefresh(token: string): void {
    const expirationTime = this.jwtHelper
      .getTokenExpirationDate(token)
      ?.getTime();
    const refreshTime = expirationTime
      ? expirationTime - TOKEN_EXPIRY_THRESHOLD_MINUTES * 60 * 1000
      : Date.now();
    const refreshInterval = refreshTime - Date.now();

    if (refreshInterval <= 0) return;

    setTimeout(() => {
      this.refreshToken().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    }, refreshInterval);
  }

  private storeTokens(data: ILoginSuccess): void {
    localStorage.setItem('token', data.token);
    localStorage.setItem('refresh_token', data.refreshToken);
  }
}
